import React, { useState, useEffect } from 'react';
import { createOrange, getAllOranges, deleteOrange, updateOrange } from '../api';

const OrangeButton = () => {
  const [oranges, setOranges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newOrangeName, setNewOrangeName] = useState('');
  const [newOrangeColor, setNewOrangeColor] = useState('');
  const [newOrangeSize, setNewOrangeSize] = useState('');
  const [editMode, setEditMode] = useState(null);
  const [editData, setEditData] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total_count: 0,
    total_pages: 1,
    has_next_page: false,
    has_prev_page: false
  });

  // Load oranges on component mount or when pagination changes
  useEffect(() => {
    fetchOranges(currentPage, pageSize);
  }, [currentPage, pageSize]);

  // Function to fetch oranges with pagination
  const fetchOranges = async (page = 1, limit = 5) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllOranges(page, limit);
      setOranges(response.oranges);
      setPagination(response.pagination);
    } catch (err) {
      setError(err.message || 'Failed to fetch oranges');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle creating a new orange
  const handleCreateOrange = async (e) => {
    e.preventDefault();
    if (!newOrangeName) {
      setError('Orange name is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const orangeData = {
        name: newOrangeName,
        color: newOrangeColor || 'orange',
        size: newOrangeSize || 'medium',
        quality: 'good'
      };

      await createOrange(orangeData);
      
      // Refresh the current page to show the new orange
      await fetchOranges(currentPage, pageSize);

      // Reset form
      setNewOrangeName('');
      setNewOrangeColor('');
      setNewOrangeSize('');
    } catch (err) {
      setError(err.message || 'Failed to create orange');
    } finally {
      setLoading(false);
    }
  };

  // Function to delete an orange
  const handleDeleteOrange = async (orangeId) => {
    setLoading(true);
    setError(null);
    try {
      await deleteOrange(orangeId);
      
      // Refresh the current page after deletion
      await fetchOranges(currentPage, pageSize);
    } catch (err) {
      setError(err.message || 'Failed to delete orange');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle entering edit mode
  const handleEditMode = (orange) => {
    setEditMode(orange.orangeId);
    setEditData({
      name: orange.name,
      color: orange.color,
      size: orange.size
    });
  };

  // Function to handle updating an orange
  const handleUpdateOrange = async (orangeId) => {
    setLoading(true);
    setError(null);
    try {
      await updateOrange(orangeId, editData);
      
      // Refresh the current page after update
      await fetchOranges(currentPage, pageSize);

      // Exit edit mode
      setEditMode(null);
    } catch (err) {
      setError(err.message || 'Failed to update orange');
    } finally {
      setLoading(false);
    }
  };
  
  // Functions to handle pagination
  const handleNextPage = () => {
    if (pagination.has_next_page) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const handlePrevPage = () => {
    if (pagination.has_prev_page) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  return (
    <div className="orange-container">

      {/* Orange Creation Form */}
      <div className="card bg-base-100 shadow-xl mb-6">
        <div className="card-body">
          <h3 className="card-title">Create New Orange</h3>
          <form onSubmit={handleCreateOrange} className="form-control gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Name</span>
              </label>
              <input
                type="text"
                value={newOrangeName}
                onChange={(e) => setNewOrangeName(e.target.value)}
                className="input input-bordered"
                placeholder="Orange name"
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Color</span>
              </label>
              <input
                type="text"
                value={newOrangeColor}
                onChange={(e) => setNewOrangeColor(e.target.value)}
                className="input input-bordered"
                placeholder="Orange color"
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Size</span>
              </label>
              <select
                value={newOrangeSize}
                onChange={(e) => setNewOrangeSize(e.target.value)}
                className="select select-bordered"
              >
                <option value="">Select size</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>

            <div className="card-actions justify-end">
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Creating...' : 'Create Orange'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-error mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span>{error}</span>
        </div>
      )}

      {/* Oranges List - Only show if there are oranges or loading */}
      {(oranges.length > 0 || loading) && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h3 className="card-title">Your Oranges</h3>

            {loading && <progress className="progress"></progress>}

            <ul className="menu bg-base-200 rounded-box mb-4">
              {oranges.map(orange => (
                <li key={orange.orangeId}>
                  {editMode === orange.orangeId ? (
                    <div className="card bg-base-200 shadow-sm">
                      <div className="card-body">
                        <div className="form-control">
                          <label className="label">
                            <span className="label-text">Name</span>
                          </label>
                          <input
                            type="text"
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            className="input input-bordered"
                          />
                        </div>

                        <div className="form-control">
                          <label className="label">
                            <span className="label-text">Color</span>
                          </label>
                          <input
                            type="text"
                            value={editData.color}
                            onChange={(e) => setEditData({ ...editData, color: e.target.value })}
                            className="input input-bordered"
                          />
                        </div>

                        <div className="form-control">
                          <label className="label">
                            <span className="label-text">Size</span>
                          </label>
                          <select
                            value={editData.size}
                            onChange={(e) => setEditData({ ...editData, size: e.target.value })}
                            className="select select-bordered"
                          >
                            <option value="small">Small</option>
                            <option value="medium">Medium</option>
                            <option value="large">Large</option>
                          </select>
                        </div>

                        <div className="card-actions justify-end">
                          <button
                            onClick={() => handleUpdateOrange(orange.orangeId)}
                            disabled={loading}
                            className="btn btn-sm btn-success"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditMode(null)}
                            className="btn btn-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col w-full">
                      <a className="w-full">
                        <div className="flex justify-between items-center w-full flex-wrap gap-2">
                          <span className="font-medium truncate max-w-[200px]">{orange.name}</span>
                          <div className="flex gap-2 shrink-0">
                            <button
                              onClick={() => handleEditMode(orange)}
                              className="btn btn-sm btn-outline btn-info"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteOrange(orange.orangeId)}
                              disabled={loading}
                              className="btn btn-sm btn-outline btn-error"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="text-sm opacity-70">
                          Color: {orange.color}, Size: {orange.size}, Quality: {orange.quality || 'N/A'}
                        </div>
                        <div className="text-xs opacity-60">
                          Created: {new Date(orange.createdAt).toLocaleString()}
                        </div>
                      </a>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* Pagination Controls */}
            {oranges.length > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="pagination-info text-sm">
                  Showing {oranges.length} of {pagination.total_count} oranges. 
                  Page {pagination.page} of {pagination.total_pages}
                </div>
                <div className="flex items-center gap-2">
                  <div className="form-control">
                    <select 
                      value={pageSize} 
                      onChange={handlePageSizeChange}
                      className="select select-bordered select-sm"
                    >
                      <option value="5">5 per page</option>
                      <option value="10">10 per page</option>
                      <option value="20">20 per page</option>
                      <option value="50">50 per page</option>
                    </select>
                  </div>
                  <div className="btn-group">
                    <button 
                      className="btn btn-sm" 
                      disabled={!pagination.has_prev_page}
                      onClick={handlePrevPage}
                    >
                      «
                    </button>
                    <button className="btn btn-sm">Page {pagination.page}</button>
                    <button 
                      className="btn btn-sm" 
                      disabled={!pagination.has_next_page}
                      onClick={handleNextPage}
                    >
                      »
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrangeButton;
